import { useInfiniteQuery } from "@tanstack/react-query";

import type { SearchCandidateResult } from "@/components/companies/jobs/search-v2/searchV2CandidateResult";
import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { SEARCH_V2_PAGE_SIZE } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { novaClient } from "@/lib/trpc";
import { SearchV2TenureFilter, SearchV2Weights } from "@/types";

export interface SearchV2CandidatesParams {
  distanceMi?: number;
  includePastSubmitted: boolean;
  includeRejected: boolean;
  location?: string;
  payCeiling?: string;
  query: string;
  relevantTenure?: SearchV2TenureFilter;
  weights?: SearchV2Weights;
  weTitle?: string[];
}

// tRPC inference collapses candidacy-derived page fields to `any`, so pages
// are treated as unknown and narrowed manually.
export const getSearchPageResults = (
  page: unknown,
): SearchCandidateResult[] => {
  if (
    !page ||
    typeof page !== "object" ||
    !Array.isArray((page as { results?: unknown }).results)
  ) {
    return [];
  }

  return (page as { results: SearchCandidateResult[] }).results;
};

// Which fetch a page came from; stamped in queryFn, so 0 for anything else.
export const getSearchPageNumber = (page: unknown): number => {
  if (!page || typeof page !== "object") return 0;
  const { pageNumber } = page as { pageNumber?: unknown };
  return typeof pageNumber === "number" ? pageNumber : 0;
};

export const getSearchPageMetrics = (page: unknown) => {
  if (!page || typeof page !== "object") {
    return null;
  }

  const searchPage = page as {
    nextPageToken?: string;
    semanticMs?: number;
    semanticResults?: number;
    textResults?: number;
    textSearchMs?: number;
    totalCount?: number;
    totalMs?: number;
  };

  return {
    has_next_page: Boolean(searchPage.nextPageToken),
    result_count: getSearchPageResults(page).length,
    semantic_ms: searchPage.semanticMs,
    semantic_result_count: searchPage.semanticResults,
    text_result_count: searchPage.textResults,
    text_search_ms: searchPage.textSearchMs,
    total_count: searchPage.totalCount,
    total_ms: searchPage.totalMs,
  };
};

export const searchV2CandidatesQueryKey = (jobId: string) =>
  ["search-v2-candidates", jobId] as const;

export const useGetSearchV2Candidates = (
  params: SearchV2CandidatesParams,
  // Off while a different corpus is driving the list (the Sally mode supplies
  // its own candidates), so we don't run a search nobody is looking at.
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const jobId = useCurrentJobId();
  const novaUtils = novaClient.useUtils();
  const {
    distanceMi,
    includePastSubmitted,
    includeRejected,
    location,
    payCeiling,
    query,
    relevantTenure,
    weights,
    weTitle,
  } = params;

  return useInfiniteQuery({
    enabled,
    // The destructured params are spread back in as one object; the plugin
    // can't see through the destructuring.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [...searchV2CandidatesQueryKey(jobId), params],
    queryFn: async ({ pageParam }) => {
      const searchPage =
        await novaUtils.internal.candidacy.jobSpecificSearch.fetch({
          jobId,
          filters: {
            distanceMi,
            includePastSubmitted: includePastSubmitted || undefined,
            includeRejected,
            keywords: query || undefined,
            location,
            payFloor: payCeiling,
            relevantTenure,
            weTitle,
          },
          limit: SEARCH_V2_PAGE_SIZE,
          offset: pageParam,
          weights,
        });

      const metrics = getSearchPageMetrics(searchPage);
      if (metrics) {
        mixpanelUtils.trackSearchResultsLoaded(MIXPANEL_SCREENS.JOB, {
          ...metrics,
          include_past_submitted: includePastSubmitted,
          include_rejected: includeRejected,
          job_id: jobId,
          keyword_count: query ? query.split(",").length : 0,
          page_token: pageParam,
          search_version: "v2",
          tenure_category: relevantTenure?.category,
          title_count: weTitle?.length ?? 0,
        });
      }

      const offset = Number(pageParam);
      return {
        ...searchPage,
        offset,
        // 1-based, what a staffer would call "page 2".
        pageNumber: Math.floor(offset / SEARCH_V2_PAGE_SIZE) + 1,
      };
    },
    initialPageParam: "0",
    getNextPageParam: (lastPage) => lastPage.nextPageToken,
  });
};
